import cx from 'classnames'
import { DateRenderer } from 'components/renderers/DateRenderer'
import { Table } from 'components/Table'
import React, { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useDataRoomsQuery } from 'utils/queries'
import { Button } from '../components/Button'
import { HorizontalLoadingIndicator } from '../components/HorizontalLoadingIndicator'
import { Icon } from '../components/Icon'
import { Layout } from '../components/Layout'
import { ListTopBar } from '../components/ListTopBar'
import { TableDataLoading } from '../components/TableDataLoading'
import { TableNoContent } from '../components/TableNoContent'
import { route } from '../utils/routes'
import { useCreateDataRoom } from '../utils/mutations'
import { useNameInputModal } from '../components/modals/NameInputModal'
import { LoadMoreButton } from '../components/LoadMoreButton'

export default function DataRoomListPage() {
    const {
        data: dataRooms,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
    } = useDataRoomsQuery({
        hasNoProject: true,
    })

    const navigate = useNavigate()

    const { t } = useTranslation()

    const createDataRoom = useCreateDataRoom()
    const openNameInputModal = useNameInputModal()

    const handleCreateDataRoom = useCallback(() => {
        openNameInputModal({
            onConfirm: async (name) => {
                await createDataRoom.mutateAsync(name)
            },
            modalTitle: `Create ${t('dataRoom.singular')}`,
            buttonCtaText: 'Save',
            buttonCtaSavingText: 'Saving',
        })
    }, [createDataRoom, openNameInputModal])

    return (
        <Layout>
            <ListTopBar />
            <HorizontalLoadingIndicator
                loading={isLoading || isFetchingNextPage}
            />
            <div
                className={cx(
                    'p-l-40',
                    'p-r-40',
                    'p-t-30',
                    'p-b-15',
                    'd-flex',
                    'align-items-center',
                    'justify-space-between'
                )}
            >
                <h1>{t('dataRoom.plural')}</h1>
                <Button $size="medium" onClick={handleCreateDataRoom}>
                    <div className="d-flex align-items-center">
                        <Icon name="plus" />
                        <span className="m-l-5">
                            Create {t('dataRoom.singular')}
                        </span>
                    </div>
                </Button>
            </div>
            <Table
                columns={[
                    {
                        title: 'Name',
                        key: 'name',
                        render: (dataRoom) => (
                            <span className="emphasized">{dataRoom.name}</span>
                        ),
                        width: 'minmax(min-content, 80%)',
                    },
                    {
                        title: 'Documents',
                        key: 'documents',
                        render: (dataRoom) => (
                            <span>{dataRoom._count.documents}</span>
                        ),
                    },
                    {
                        title: 'Last update',
                        key: 'updatedAt',
                        render: (dataRoom) => (
                            <DateRenderer value={dataRoom.updatedAt} />
                        ),
                    },
                ]}
                data={dataRooms}
                onRowClick={(dataRoom) => {
                    navigate(route('dataRoom', dataRoom.id))
                }}
                stickyHeader
            />
            {isLoading && <TableDataLoading />}
            {!isLoading && dataRooms.length === 0 && (
                <TableNoContent
                    icon="database"
                    title={`Create the first ${t('dataRoom.singular')}`}
                    description={`${t('dataRoom.plural')} deliver additional information to connected ${t('assessment.plural')}.`}
                    buttonText={`Create ${t('dataRoom.singular')}`}
                    buttonOnClick={handleCreateDataRoom}
                />
            )}
            {hasNextPage && (
                <LoadMoreButton
                    onClick={fetchNextPage}
                    isLoading={isFetchingNextPage}
                />
            )}
        </Layout>
    )
}
